import Editor, { type OnMount } from "@monaco-editor/react";
import type * as Monaco from "monaco-editor";
import { useEffect, useRef } from "react";
import { useSession } from "../app/store";
import "./monacoSetup";

export function languageFor(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "py":
      return "python";
    case "yaml":
    case "yml":
      return "yaml";
    case "json":
      return "json";
    case "csv":
      return "plaintext";
    case "j2":
    case "jinja":
    case "jinja2":
    case "txt":
      return "jinja";
    case "html":
      return "html";
    case "md":
      return "markdown";
    default:
      return "plaintext";
  }
}

export interface CodeEditorProps {
  path: string;
  value: string;
  onChange?: (v: string) => void;
  onSave?: () => void;
  readOnly?: boolean;
  language?: string;
  markers?: { line: number; col: number; end_line?: number; end_col?: number; message: string; severity: "error" | "warning" | "info" }[];
  onMount?: OnMount;
  wordWrap?: boolean;
}

export function CodeEditor({ path, value, onChange, onSave, readOnly, language, markers, onMount, wordWrap }: CodeEditorProps) {
  const theme = useSession((s) => s.theme);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const saveRef = useRef(onSave);
  saveRef.current = onSave;

  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => saveRef.current?.());
    onMount?.(editor, monaco);
  };

  useEffect(() => {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    const model = ed?.getModel();
    if (!ed || !monaco || !model) return;
    const sev = { error: monaco.MarkerSeverity.Error, warning: monaco.MarkerSeverity.Warning, info: monaco.MarkerSeverity.Info };
    monaco.editor.setModelMarkers(
      model,
      "reports-studio",
      (markers ?? []).map((m) => ({
        severity: sev[m.severity],
        message: m.message,
        startLineNumber: m.line || 1,
        startColumn: (m.col || 0) + 1,
        endLineNumber: m.end_line || m.line || 1,
        endColumn: m.end_col !== undefined ? m.end_col + 1 : (m.col || 0) + 2,
      })),
    );
  }, [markers]);

  return (
    <Editor
      path={path}
      language={language ?? languageFor(path)}
      theme={theme === "dark" ? "rs-dark" : "rs-light"}
      value={value}
      onChange={(v) => onChange?.(v ?? "")}
      onMount={handleMount}
      options={{
        readOnly,
        minimap: { enabled: false },
        fontSize: 13,
        fontFamily: '"Cascadia Code", "JetBrains Mono", Consolas, monospace',
        scrollBeyondLastLine: false,
        renderLineHighlight: "line",
        glyphMargin: true,
        automaticLayout: true,
        wordWrap: wordWrap ? "on" : "off",
        tabSize: 4,
      }}
    />
  );
}
